import { useTranslation } from 'react-i18next';
import { Calendar, AlertTriangle } from 'lucide-react';
import { format, differenceInDays } from 'date-fns';
import { he } from 'date-fns/locale';
import type { DeadlineStatus } from '../types';

interface DeadlineBadgeProps {
  dueDate?: string | null;
  source?: 'ticket' | 'milestone' | 'project' | null;
  warningDays?: number;
  size?: 'sm' | 'md';
}

const sourceKeys: Record<string, string> = {
  ticket: 'deadline.ticketSource',
  milestone: 'deadline.milestoneSource',
  project: 'deadline.projectSource',
};

export function getDeadlineStatus(
  dueDate: string | null | undefined,
  warningDays: number = 7
): DeadlineStatus {
  if (!dueDate) return 'ok';
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  const due = new Date(dueDate);
  due.setHours(0, 0, 0, 0);
  const diffDays = differenceInDays(due, now);
  if (diffDays < 0) return 'overdue';
  if (diffDays <= warningDays) return 'approaching';
  return 'ok';
}

export default function DeadlineBadge({ dueDate, source, warningDays = 7, size = 'sm' }: DeadlineBadgeProps) {
  const { t } = useTranslation();
  if (!dueDate) return null;

  const status = getDeadlineStatus(dueDate, warningDays);
  const due = new Date(dueDate);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  const diffDays = differenceInDays(due, now);

  const isSmall = size === 'sm';
  const iconSize = isSmall ? 10 : 12;

  const statusConfig = {
    ok: {
      bg: 'bg-surface-100',
      text: 'text-surface-600',
      icon: Calendar,
    },
    approaching: {
      bg: 'bg-warning-100',
      text: 'text-warning-700',
      icon: AlertTriangle,
    },
    overdue: {
      bg: 'bg-danger-100 animate-pulse',
      text: 'text-danger-700',
      icon: AlertTriangle,
    },
  };

  const config = statusConfig[status];
  const Icon = config.icon;

  const dateStr = format(due, 'dd/MM/yyyy', { locale: he });

  let label = dateStr;
  if (status === 'overdue') {
    label = t('deadline.overdue', { count: Math.abs(diffDays) });
  } else if (status === 'approaching') {
    label = diffDays === 0 ? t('deadline.overdueToday') : t('deadline.approaching', { count: diffDays });
  }

  return (
    <span className={`inline-flex items-center gap-1 ${isSmall ? 'px-1.5 py-0.5 text-xs' : 'px-2 py-1 text-xs'} rounded-full ${config.bg} ${config.text}`}>
      <Icon size={iconSize} />
      <span>{label}</span>
      {source && source !== 'ticket' && (
        <span className="opacity-60">({t(sourceKeys[source])})</span>
      )}
    </span>
  );
}
